import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Review } from '../../services/reviewService';
import { DEFAULT_AVATAR_URL } from '../../constants/assets';

const DIMENSION_LABELS: Record<string, string> = {
  outputQuality: '作品质量',
  communication: '沟通效率',
  timeliness: '交付准时',
  professionalism: '专业程度',
  requirementClarity: '需求清晰',
  paymentTimeliness: '付款及时',
  cooperationAttitude: '合作态度',
};

const Stars: React.FC<{ rating: number; size?: string }> = ({ rating, size = 'w-3.5 h-3.5' }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`${size} ${s <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
    ))}
  </div>
);

interface ReviewCardProps {
  review: Review;
  reviewerName?: string;
  reviewerAvatar?: string;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({ review, reviewerName, reviewerAvatar }) => {
  const navigate = useNavigate();
  const formatDate = (ts: number) => new Date(ts).toLocaleDateString('zh-CN');
  const dims = review.dimensionRatings || {};

  return (
    <div className="p-4 border border-gray-100 rounded-xl">
      <div className="flex items-start gap-3">
        <img
          src={reviewerAvatar || DEFAULT_AVATAR_URL}
          alt=""
          className="w-10 h-10 rounded-full object-cover border border-gray-100 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate(`/profile/${review.reviewerUserId}`)}
        />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="font-medium text-gray-900 text-sm cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/profile/${review.reviewerUserId}`)}
              >{reviewerName || review.reviewerUserId}</span>
              <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                {review.role === 'client_to_creator' ? '客户评价' : '创作者评价'}
              </span>
            </div>
            <span className="text-xs text-gray-400">{formatDate(review.createdAt)}</span>
          </div>

          {/* Overall rating */}
          <div className="flex items-center gap-2 mt-1.5">
            <Stars rating={review.rating} />
            <span className="text-sm font-semibold text-yellow-500">{review.rating}</span>
          </div>

          {/* Dimension ratings */}
          {Object.keys(dims).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {Object.entries(dims).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>{DIMENSION_LABELS[key] || key}</span>
                  <Stars rating={val} size="w-2.5 h-2.5" />
                </div>
              ))}
            </div>
          )}

          <p className="text-sm text-gray-600 mt-2.5 leading-relaxed">{review.comment}</p>
        </div>
      </div>
    </div>
  );
};
